import { ReactNode, useEffect, useState, useCallback } from "react";
import { X, CheckCircle2, AlertCircle, Info, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";

export type ToastType = "success" | "error" | "info" | "warning";

interface ToastProps {
  id: string;
  type: ToastType;
  message: string;
  description?: string;
  duration?: number;
  onClose: (id: string) => void;
}

export function Toast({
  id,
  type,
  message,
  description,
  duration = 5000,
  onClose,
}: ToastProps) {
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  }, [onClose, id]);

  useEffect(() => {
    if (duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, handleClose]);

  const icons: Record<ToastType, ReactNode> = {
    success: <CheckCircle2 className="h-5 w-5 text-success" />,
    error: <AlertCircle className="h-5 w-5 text-destructive" />,
    info: <Info className="h-5 w-5 text-primary" />,
    warning: <AlertTriangle className="h-5 w-5 text-warning" />,
  };

  const bgClasses: Record<ToastType, string> = {
    success: "border-success/20",
    error: "border-destructive/20",
    info: "border-primary/20",
    warning: "border-warning/20",
  };

  return (
    <div
      className={cn(
        "pointer-events-auto w-full sm:max-w-sm rounded-lg border-2 bg-card p-4 shadow-lg backdrop-blur-sm transition-all duration-300",
        bgClasses[type],
        isExiting
          ? "sm:translate-x-full -translate-y-full sm:translate-y-0 opacity-0"
          : "translate-x-0 translate-y-0 opacity-100 animate-slide-down sm:animate-slide-up"
      )}
      role="alert"
      aria-live="polite"
    >
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0">{icons[type]}</div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">{message}</p>
          {description && (
            <p className="mt-1 text-sm text-muted-foreground">{description}</p>
          )}
        </div>
        <button
          onClick={handleClose}
          className="flex-shrink-0 rounded-lg p-1 hover:bg-muted transition-colors"
          aria-label="Zamknij"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// Toast Container
interface ToastContainerProps {
  toasts: Array<{
    id: string;
    type: ToastType;
    message: string;
    description?: string;
    duration?: number;
  }>;
  onClose: (id: string) => void;
}

export function ToastContainer({ toasts, onClose }: ToastContainerProps) {
  return (
    <div
      className="fixed top-4 left-4 right-4 sm:top-auto sm:left-auto sm:bottom-4 sm:right-4 z-[10000] flex flex-col gap-2 pointer-events-none"
      aria-label="Powiadomienia"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={onClose} />
      ))}
    </div>
  );
}

// Hook to manage toasts
// eslint-disable-next-line react-refresh/only-export-components
export function useToast() {
  const [toasts, setToasts] = useState<
    Array<{
      id: string;
      type: ToastType;
      message: string;
      description?: string;
      duration?: number;
    }>
  >([]);

  const addToast = (
    type: ToastType,
    message: string,
    description?: string,
    duration = 5000
  ) => {
    const id = Math.random().toString(36).substr(2, 9);
    setToasts((prev) => [...prev, { id, type, message, description, duration }]);
  };

  const removeToast = (id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  };

  return {
    toasts,
    addToast,
    removeToast,
    success: (message: string, description?: string) =>
      addToast("success", message, description),
    error: (message: string, description?: string) =>
      addToast("error", message, description),
    info: (message: string, description?: string) =>
      addToast("info", message, description),
    warning: (message: string, description?: string) =>
      addToast("warning", message, description),
  };
}

